import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { bodyWidth } from '../../../../utils/Style';
import { AccountNavbar } from '../../AccountNavbar';
import { useUserSummary } from '../../../../hooks/useUserSummary';
import { updateUserRequestFromUserSummary } from '../../../../interfaces/User/UpdateUserRequest';
import { validatePhoneNumber } from '../../../../utils/Validation';
import { updateUser } from '../../../../api/User';
import { useThemeColor } from '../../../../hooks/useThemeColor';
import { GenericHelmet } from '../../../../components/Helmet/GenericHelmet';

export const EditProfilePage = () => {
  const { primary, secondary } = useThemeColor();

  const {
    userSummary,
    refreshUserSummary,
    loading: loadingUserSummary,
  } = useUserSummary();

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState(
    updateUserRequestFromUserSummary(userSummary),
  );

  useEffect(() => {
    setValues(updateUserRequestFromUserSummary(userSummary));
  }, [userSummary]);

  const handleSubmit = useCallback(() => {
    if (!userSummary) {
      return;
    }

    setLoading(true);
    updateUser(userSummary.id, values)
      .then(() => {
        toast.success('Profil başarıyla güncellendi');
        refreshUserSummary();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [userSummary, values]);

  const isFirstNameValid = values.firstName.length > 0;
  const isLastNameValid = values.lastName.length > 0;
  const isPhoneValid = validatePhoneNumber(values.phone);

  const resolvedLoading = loading || loadingUserSummary;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet title='Profili Düzenle' />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          pb: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <AccountNavbar selectedTab='profile' />

        <Paper
          elevation={3}
          sx={{
            px: 2.5,
            py: 2,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            maxWidth: 800,
          }}
        >
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
            Profili Düzenle
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>İsim</Typography>

            <TextField
              size='small'
              fullWidth
              disabled={resolvedLoading}
              value={values.firstName}
              onChange={(e) =>
                setValues({ ...values, firstName: e.target.value })
              }
              error={!isFirstNameValid}
              helperText={isFirstNameValid ? undefined : 'Bu alan zorunludur'}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>Soyisim</Typography>

            <TextField
              size='small'
              fullWidth
              disabled={resolvedLoading}
              value={values.lastName}
              onChange={(e) =>
                setValues({ ...values, lastName: e.target.value })
              }
              error={!isLastNameValid}
              helperText={isLastNameValid ? undefined : 'Bu alan zorunludur'}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>E-Posta</Typography>

            <TextField size='small' fullWidth disabled value={values.email} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>Telefon</Typography>

            <TextField
              size='small'
              fullWidth
              disabled={resolvedLoading}
              value={values.phone}
              onChange={(e) => setValues({ ...values, phone: e.target.value })}
              error={!isPhoneValid}
              helperText={
                isPhoneValid
                  ? undefined
                  : 'Telefon numarası 11 karakterden oluşmalı ve başında 0 olmalıdır'
              }
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={
                resolvedLoading ||
                !isFirstNameValid ||
                !isPhoneValid ||
                !isLastNameValid
              }
              sx={{
                textTransform: 'none',
                backgroundColor: primary,
                color: secondary,
              }}
            >
              Kaydet
            </Button>

            {resolvedLoading && <CircularProgress color='primary' />}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
